import React, { useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { range, pick, uniq, omit } from 'ramda';
import {
  Stack,
  Input,
  Select,
  CreditCardNumberInput,
  Button,
  Text,
  Loading,
  Checkbox,
  Overlay
} from '@moda/om';
import { throwQueryError } from '../../../lib/apollo/throwQueryError';
import { reportError } from '../../../lib/reportError';
import { YEARS_TO_MONTHS } from '../../../constants';
import { useForm } from '../../../hooks/useForm';
import { useUser } from '../../../hooks/useUser';
import { usePaymentInfoEnteredTracking } from '../../../hooks/useCheckout';
import { useAddressesQuery, RestAddress } from '../../../hooks/useAddress';
import {
  useCreatePaymentMethodMutation,
  useUpdatePaymentMethodMutation,
  PaymentMethod
} from '../../../hooks/usePaymentMethods';
import { LoadingBalls } from '../../LoadingBalls';
import { validations as ADDRESS_VALIDATIONS } from '../../ShippingAddress/ShippingAddressForm/validations';
import { ShippingAddressFormFields } from '../../ShippingAddress';
import { usePaymentError } from '../../../hooks/useCheckout/usePaymentError';
import { CARD_VALIDATIONS } from './validations';
import { humanizeAddress } from './humanizeAddress';
import { createMaskWithLastFour } from './createMaskWithLastFour';
import { convertBillingAddressToShippingAddress } from './convertBillingAddressToShippingAddress';
import { convertShippingAddressToBillingAddress } from './convertShippingAddressToBillingAddress';

import './PaymentMethodForm.scss';

const TEN = 10;
const MAX_EXPIRATION_YEARS = 19;
const CURRENT_YEAR = new Date().getFullYear();

const ADDRESS_VALIDATION_FIELDS = ['addressLine1', 'addressLine2', 'city', 'state', 'postalCode'];

enum Mode {
  Resting,
  Saving
}

interface Props {
  method?: PaymentMethod;
  submitButtonLabel?: string;
  confirmCVV?: boolean;
  onCancel?: () => void;
  onSave: (method?: PaymentMethod) => void | Promise<void>;
}

export const PaymentMethodForm: React.FC<Props> = ({
  method,
  submitButtonLabel = 'Save',
  confirmCVV,
  onCancel,
  onSave
}) => {
  const isNew = !method;
  const billingAddress = method?.attributes.billingAddress;
  const { user } = useUser();
  const { paymentError, setPaymentError } = usePaymentError();

  const [mode, setMode] = useState(Mode.Resting);

  const [errorMessage, setErrorMessage] = useState('');

  const [useExistingAddress, setUseExistingAddress] = useState(
    isNew ? false : Boolean(billingAddress?.id)
  );

  const { addresses, error, loading } = useAddressesQuery();

  const [createPaymentMethod] = useCreatePaymentMethodMutation();
  const [updatePaymentMethod] = useUpdatePaymentMethodMutation();
  const { trackPaymentInfoEntered } = usePaymentInfoEnteredTracking();

  const onSubmit = useCallback(
    async fields => {
      setMode(Mode.Saving);
      setErrorMessage('');
      if (paymentError) setPaymentError(null);
      try {
        const address = fields.addressId
          ? addresses.find(address => address.id === fields.addressId)
          : ({ id: '', type: 'addresses', attributes: fields } as RestAddress);

        if (!address) throw new Error('PaymentMethodForm: address not found');

        const billingAddress = convertShippingAddressToBillingAddress(address);

        const commonArgs = {
          cardholderName: fields.cardholderName,
          expirationMonth:
            Number(fields.expirationMonth) < TEN
              ? `0${fields.expirationMonth}`
              : fields.expirationMonth,
          expirationYear: fields.expirationYear,
          securityCode: fields.securityCode,
          billingAddress
        };

        const result = method
          ? (
              await updatePaymentMethod({
                id: method.id,
                ...commonArgs
              })
            ).data?.updatePaymentMethod.data
          : (
              await createPaymentMethod({
                cardNumber: fields.cardNumber.replace(/\s/g, ''),
                ...commonArgs
              })
            ).data?.createPaymentMethod.data;

        await onSave(result);
        trackPaymentInfoEntered();
      } catch (error) {
        const networkErrorMessage = error.networkError?.result?.errors?.[0]?.detail;
        const message =
          networkErrorMessage || error.displayMessage || 'An error occurred. Please retry later.';

        setErrorMessage(message);
        reportError(error);

        window.newrelic?.addPageAction('paymentMethodFailed', {
          error: error.toString(),
          networkErrorMessage,
          message,
          email: user.email,
          guest: !user.isLoggedIn
        });
      }
      setMode(Mode.Resting);
    },
    [
      paymentError,
      setPaymentError,
      addresses,
      method,
      updatePaymentMethod,
      createPaymentMethod,
      onSave,
      trackPaymentInfoEntered,
      user
    ]
  );

  const cardValidations = isNew ? CARD_VALIDATIONS : omit(['cardNumber'], CARD_VALIDATIONS);

  const { handleSubmit, errors } = useForm({
    onSubmit,
    validations: useExistingAddress
      ? cardValidations
      : { ...cardValidations, ...pick(ADDRESS_VALIDATION_FIELDS, ADDRESS_VALIDATIONS) }
  });

  const [defaultExpirationMonth, defaultExpirationYear] = method?.attributes.expirationDate.split(
    '/'
  ) || ['1', CURRENT_YEAR.toString()];

  useEffect(() => {
    addresses.length > 0 && isNew && setUseExistingAddress(true);
  }, [addresses, isNew, setUseExistingAddress]);

  if (error) throwQueryError(error);

  return (
    <>
      <form
        className={classNames('PaymentMethodForm', { 'PaymentMethodForm--error': errorMessage })}
        onSubmit={handleSubmit}
      >
        <Stack space={4}>
          <div className="PaymentMethodForm__name-number">
            <Input
              className="PaymentMethodForm__input"
              label="Card Holder's Name"
              name="cardholderName"
              error={errors.cardholderName?.message}
              disabled={!isNew}
              defaultValue={isNew ? '' : method?.attributes.cardholderName}
            />
            {isNew ? (
              <CreditCardNumberInput
                className="PaymentMethodForm__input"
                maskChar=""
                label="Card Number"
                name="cardNumber"
                error={errors.cardNumber?.message}
              />
            ) : (
              <Input
                className="PaymentMethodForm__input"
                label="Card Number"
                name="cardNumber"
                disabled
                defaultValue={createMaskWithLastFour(method)}
              />
            )}
          </div>
          <div className="PaymentMethodForm__expiration-cvv">
            <Stack className="PaymentMethodForm__expiration" direction="horizontal" space={4}>
              <Input
                className="PaymentMethodForm__input"
                label="Exp Month"
                name="expirationMonth"
                error={errors.expirationMonth?.message}
                value={isNew ? '' : defaultExpirationMonth}
              >
                <Select
                  defaultValue={defaultExpirationMonth}
                  label=""
                  options={uniq([
                    ...range(1, YEARS_TO_MONTHS + 1),
                    Number(defaultExpirationMonth)
                  ]).map((month: number) => ({
                    label: month < TEN ? `0${month}` : `${month}`,
                    value: month.toString()
                  }))}
                />
              </Input>
              <Input
                className="PaymentMethodForm__input"
                label="Exp Year"
                name="expirationYear"
                error={errors.expirationYear?.message}
                value={isNew ? '' : defaultExpirationYear}
              >
                <Select
                  defaultValue={defaultExpirationYear}
                  label=""
                  options={uniq([
                    ...range(CURRENT_YEAR, CURRENT_YEAR + MAX_EXPIRATION_YEARS),
                    Number(defaultExpirationYear)
                  ]).map((year: number) => ({
                    label: year.toString(),
                    value: year.toString()
                  }))}
                />
              </Input>
            </Stack>
            <Input
              className="PaymentMethodForm__input"
              label="Security Code"
              name="securityCode"
              error={
                errors.securityCode?.message ||
                (confirmCVV ? 'Please enter your 3 or 4 digit CVV code.' : undefined)
              }
            />
          </div>
          <div className="PaymentMethodForm__divider" />
          {loading && <Loading />}
          {!loading && (
            <>
              <Text treatment="eyebrow">Billing Address</Text>
              {addresses.length > 0 && (
                <Checkbox
                  checked={useExistingAddress}
                  onChange={event => setUseExistingAddress(event.target.checked)}
                >
                  Use an existing address
                </Checkbox>
              )}
              {useExistingAddress && (
                <Select
                  name="addressId"
                  options={addresses.map(address => ({
                    label: humanizeAddress(address),
                    value: address.id
                  }))}
                  defaultValue={
                    billingAddress?.id &&
                    addresses.some(address => address.id === billingAddress.id)
                      ? billingAddress.id
                      : addresses[0]?.id
                  }
                />
              )}

              {!useExistingAddress && (
                <ShippingAddressFormFields
                  address={
                    isNew || billingAddress?.id
                      ? undefined
                      : convertBillingAddressToShippingAddress(billingAddress)
                  }
                  version="compact"
                  mutationFieldErrors={undefined}
                  validationErrors={errors}
                />
              )}
            </>
          )}

          {errorMessage && (
            <Text treatment="body1" color="code-red">
              {errorMessage}
            </Text>
          )}

          <Stack space={2} direction="horizontal" justifyContent="flex-end">
            {onCancel && (
              <Button
                className="PaymentMethodForm__button"
                secondary
                type="button"
                onClick={onCancel}
                data-testid="PaymentMethodForm__button"
              >
                Cancel
              </Button>
            )}
            <Button className="PaymentMethodForm__button" type="submit">
              {submitButtonLabel}
            </Button>
          </Stack>
        </Stack>
      </form>

      <Overlay show={mode === Mode.Saving}>
        <LoadingBalls />

        <Text treatment="eyebrow">Saving</Text>
      </Overlay>
    </>
  );
};
